import { useCallback, useMemo } from 'react';

import { ColumnDef } from '@tanstack/react-table';
import BigNumber from 'bignumber.js';
import { PositionView } from '@paradigm/features/src/positions/entities/positions';
import useIsMobile from '@paradigm/design-system/src/hooks/useIsMobile';
import Checkbox from '@paradigm/design-system/src/components/Checkbox';
import { CheckboxContainer } from '@paradigm/design-system/src/components/Table';
import {
  AccountCell,
  ActionsCell,
  FormattedCell,
  TextCell,
  TextColored,
} from './Cells';
import {
  aggregateGreekSum,
  aggregatedQuantitySum,
  aggregatedUnrealizedPnl,
} from './aggregationFunctions';
import {
  sortExpiry,
  sortQuantity,
  sortRowStringsByKey,
  sortUnrealizedPnl,
} from './sortingFunctions';
import type { Column } from '@paradigm/design-system/src/components/Table';

export default function useColumns() {
  const isMobile = useIsMobile();
  const columns = useMemo<Column<PositionView>[]>(
    () => [
      {
        id: 'select',
        header: ' ',
        size: !isMobile ? 32 : 24,
        enableResizing: false,
        meta: {
          isSelectColumn: true,
        },

        cell: ({ row }) => {
          return (
            <CheckboxContainer aria-label="select position row">
              <Checkbox
                onCheck={(e) => {
                  row.getToggleSelectedHandler()(e);
                }}
                indeterminate={row.getIsSomeSelected()}
                isChecked={row.getIsSelected()}
                dataTestId={`positions-table-checkbox-row-${row.original.id}`}
              />
            </CheckboxContainer>
          );
        },
      },
      {
        id: 'product',
        accessorKey: 'product',
        meta: { isGroupingColumn: true, draggable: false },
        header: 'Product',
        sortingFn: sortRowStringsByKey('product'),
        cell: (info) => <TextCell info={info} dataTestid="product" />,
        size: 160,
        enableResizing: false,
      },
      {
        id: 'description',
        accessorKey: 'description',
        header: 'Description',
        cell: (info) => <TextCell info={info} dataTestid="description" />,
        size: !isMobile ? 220 : undefined,
        enableResizing: false,
        enableSorting: false,
        meta: { isMobileHeader: true },
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: (info) => <ActionsCell info={info} />,
        aggregatedCell: (info) => <ActionsCell info={info} />,
        size: 120,
        enableResizing: false,
        meta: { isMobileLabelHidden: true },
      },
      {
        id: 'quantity',
        accessorKey: 'quantity',
        header: 'Quantity',
        sortingFn: sortQuantity,
        aggregationFn: aggregatedQuantitySum,
        aggregatedCell: (info) => (
          <TextCell info={info} dataTestid="quantity" />
        ),
        cell: (info) => (
          <FormattedCell<BigNumber> info={info} dataTestid="quantity" />
        ),
        size: 90,
        enableResizing: false,
      },
      {
        id: 'clearing_currency',
        accessorKey: 'clearing_currency',
        aggregationFn: 'unique',
        aggregatedCell: (info) => <TextCell info={info} />,
        header: 'Clearing Currency',
        sortingFn: sortRowStringsByKey('clearing_currency'),

        cell: (info) => <TextCell info={info} />,
        size: 146,
        enableResizing: false,
      },
      {
        id: 'unrealizedPnl',
        accessorKey: 'unrealizedPnl',
        header: 'Unrealized PnL',
        sortingFn: sortUnrealizedPnl,
        aggregationFn: aggregatedUnrealizedPnl,
        aggregatedCell: (info) => <TextColored info={info} />,
        cell: (info) => <TextColored info={info} />,
        size: 128,
        enableResizing: false,
      },
      {
        id: 'venue',
        accessorKey: 'venue',
        header: 'Venue',
        aggregationFn: 'unique',
        aggregatedCell: (info) => <TextCell info={info} />,
        cell: (info) => <TextCell info={info} />,
        size: 76,
        enableResizing: false,
      },
      {
        id: 'accountName',
        accessorKey: 'accountName',
        header: 'Account',
        aggregationFn: 'unique',
        sortingFn: sortRowStringsByKey('accountName'),
        aggregatedCell: (info) => <AccountCell info={info} />,
        cell: (info) => <AccountCell info={info} />,
        size: 130,
        enableResizing: false,
      },
      {
        id: 'markPrice',
        accessorKey: 'markPrice',
        header: 'Mark',
        enableSorting: false,
        cell: (info) => <TextCell info={info} />,
        size: 80,
        enableResizing: false,
      },
      {
        id: 'delta',
        accessorFn: (position) => position.greeks.delta,
        header: 'Delta (∆)',
        aggregationFn: aggregateGreekSum,
        aggregatedCell: (info) => <TextCell info={info} />,
        enableSorting: false,
        cell: (info) => <FormattedCell info={info} />,
        size: 80,
        enableResizing: false,
      },
      {
        id: 'gamma',
        accessorFn: (position) => position.greeks.gamma,
        enableSorting: false,
        header: 'Gamma (Γ)',
        aggregationFn: aggregateGreekSum,
        aggregatedCell: (info) => <TextCell info={info} />,
        cell: (info) => <FormattedCell info={info} />,
        size: 84,
        enableResizing: false,
      },
      {
        id: 'theta',
        accessorFn: (position) => position.greeks.theta,
        header: 'Theta (Θ)',
        enableSorting: false,
        aggregationFn: aggregateGreekSum,
        aggregatedCell: (info) => <TextCell info={info} />,
        cell: (info) => <FormattedCell info={info} />,
        size: 86,
        enableResizing: false,
      },
      {
        id: 'vega',
        accessorFn: (position) => position.greeks.vega,
        aggregationFn: aggregateGreekSum,
        enableSorting: false,
        aggregatedCell: (info) => <TextCell info={info} />,
        header: 'Vega (V)',
        cell: (info) => <FormattedCell info={info} />,
        size: 68,
        enableResizing: false,
      },
      {
        id: 'expires_at',
        accessorFn: (position) => position.expiresAt.formatted,
        sortingFn: sortExpiry,
        accessorKey: 'expiresAt',
        header: 'Expires',
        cell: (info) => <TextCell info={info} />,
        size: 100,
        enableResizing: false,
      },
      {
        id: 'strike',
        accessorKey: 'strike',
        header: 'Strike',
        cell: (info) => <TextCell info={info} />,
        size: 100,
        enableResizing: false,
      },
      {
        id: 'option_kind',
        accessorKey: 'option_kind',
        header: 'Option Kind',
        cell: (info) => <TextCell info={info} />,
        size: 100,
        enableResizing: false,
      },
      {
        id: 'account_name',
        accessorKey: 'accountName',
        header: 'Account',
        sortingFn: 'alphanumericCaseSensitive',
        cell: (info) => <TextCell info={info} />,
        size: 120,
        enableResizing: false,
      },
    ],
    [isMobile],
  );

  const extractColumns = useCallback(
    (
      columnDefs:
        | Column<PositionView>[]
        | ColumnDef<PositionView, { meta: { isSelectColumn: boolean } }>[],
    ): string[] => {
      const columnIds: string[] = [];
      columnDefs.forEach((column) => {
        if (column.id != null) columnIds.push(column.id);
      });

      return columnIds;
    },
    [],
  );

  const defaultColumns = useMemo(
    () => extractColumns(columns),
    [columns, extractColumns],
  );

  return { columns, defaultColumns };
}
